import { create } from 'zustand';
import {
  authForgotPassword,
  authLogin,
  authLogout,
  authLogoutAll,
  authMe,
  refreshSession,
  authResendVerification,
  authResetPassword,
  authSignup,
  authVerifyEmail,
  AuthApiError,
} from '../api/authApi';
import { clearStoredTokens, getStoredAccessToken, getStoredRefreshToken } from '../api/authStorage';
import type { AuthUser, LoginCredentials, RegisterPayload } from '../types/auth';

let bootstrapPromise: Promise<void> | null = null;

export type RegisterOutcome = 'verification_required' | 'session';

interface AuthStore {
  user: AuthUser | null;
  loading: boolean;
  authSubmitting: boolean;
  initialized: boolean;
  error: string | null;
  info: string | null;
  pendingVerificationEmail: string | null;
  clearError: () => void;
  clearInfo: () => void;
  bootstrap: () => Promise<void>;
  login: (creds: LoginCredentials) => Promise<boolean>;
  register: (payload: RegisterPayload) => Promise<RegisterOutcome | null>;
  verifyEmail: (token: string) => Promise<boolean>;
  resendVerification: (email: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<boolean>;
  resetPassword: (token: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  logoutAll: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  loading: true,
  authSubmitting: false,
  initialized: false,
  error: null,
  info: null,
  pendingVerificationEmail: null,

  clearError: () => set({ error: null }),
  clearInfo: () => set({ info: null }),

  bootstrap: async () => {
    if (get().initialized) return;
    if (bootstrapPromise) return bootstrapPromise;

    const run = async () => {
      const access = getStoredAccessToken();
      const refresh = getStoredRefreshToken();
      if (!access && !refresh) {
        set({ user: null, loading: false, initialized: true });
        return;
      }

      try {
        if (access) {
          try {
            const me = await authMe();
            set({ user: me });
            return;
          } catch (e) {
            if (e instanceof AuthApiError && e.status === 401 && refresh) {
              await refreshSession();
              const me = await authMe();
              set({ user: me });
              return;
            }
            throw e;
          }
        }
        if (refresh) {
          await refreshSession();
          const me = await authMe();
          set({ user: me });
        }
      } catch {
        clearStoredTokens();
        set({ user: null });
      } finally {
        set({ loading: false, initialized: true });
      }
    };

    bootstrapPromise = run().finally(() => {
      bootstrapPromise = null;
    });
    return bootstrapPromise;
  },

  refreshUser: async () => {
    const me = await authMe();
    set({ user: me });
  },

  login: async (creds) => {
    set({ error: null, info: null, authSubmitting: true });
    const email = creds.email.trim().toLowerCase();
    try {
      await authLogin({ ...creds, email });
      const me = await authMe();
      set({ user: me, pendingVerificationEmail: null });
      return true;
    } catch (e) {
      const msg = e instanceof AuthApiError ? e.message : 'Sign in failed. Try again.';
      const needsVerify = /not verified|verify your/i.test(msg);
      set({
        error: msg,
        pendingVerificationEmail: needsVerify ? email : get().pendingVerificationEmail,
      });
      return false;
    } finally {
      set({ authSubmitting: false });
    }
  },

  register: async (payload) => {
    set({ error: null, info: null, authSubmitting: true });
    try {
      const result = await authSignup(payload);
      if (result.kind === 'verification_required') {
        set({
          pendingVerificationEmail: result.email,
          info: result.message,
          user: null,
        });
        return 'verification_required';
      }
      const me = await authMe();
      set({ user: me, pendingVerificationEmail: null });
      return 'session';
    } catch (e) {
      const msg = e instanceof AuthApiError ? e.message : 'Sign up failed. Try again.';
      set({ error: msg });
      return null;
    } finally {
      set({ authSubmitting: false });
    }
  },

  verifyEmail: async (token) => {
    set({ error: null, info: null, authSubmitting: true });
    try {
      await authVerifyEmail({ token });
      const me = await authMe();
      set({ user: me, pendingVerificationEmail: null });
      return true;
    } catch (e) {
      const msg =
        e instanceof AuthApiError ? e.message : 'Verification failed. Request a new link.';
      set({ error: msg });
      return false;
    } finally {
      set({ authSubmitting: false });
    }
  },

  resendVerification: async (email) => {
    set({ error: null, info: null, authSubmitting: true });
    try {
      await authResendVerification({ email: email.trim().toLowerCase() });
      set({
        info:
          'If an unverified account exists for that email, a verification link has been sent.',
        pendingVerificationEmail: email.trim().toLowerCase(),
      });
    } catch (e) {
      const msg = e instanceof AuthApiError ? e.message : 'Could not resend verification email.';
      set({ error: msg });
    } finally {
      set({ authSubmitting: false });
    }
  },

  forgotPassword: async (email) => {
    set({ error: null, info: null, authSubmitting: true });
    try {
      await authForgotPassword({ email: email.trim().toLowerCase() });
      set({
        info: 'If an account exists for that email, a password reset link has been sent.',
      });
      return true;
    } catch (e) {
      const msg = e instanceof AuthApiError ? e.message : 'Could not send reset email.';
      set({ error: msg });
      return false;
    } finally {
      set({ authSubmitting: false });
    }
  },

  resetPassword: async (token, password) => {
    set({ error: null, info: null, authSubmitting: true });
    try {
      await authResetPassword({ token, password });
      set({ info: 'Password updated. Please sign in with your new password.' });
      return true;
    } catch (e) {
      const msg = e instanceof AuthApiError ? e.message : 'Could not reset password.';
      set({ error: msg });
      return false;
    } finally {
      set({ authSubmitting: false });
    }
  },

  logout: async () => {
    set({ error: null });
    await authLogout();
    set({ user: null, pendingVerificationEmail: null });
  },

  logoutAll: async () => {
    set({ error: null });
    await authLogoutAll();
    set({ user: null, pendingVerificationEmail: null });
  },
}));
